import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import Edit2 from "react-feather/dist/icons/edit-2";
import Trash from "react-feather/dist/icons/trash";

import style from "./style.module.scss";
import PageContent from "../page-content";
import { Button, Notice, ConfirmDialog } from "../../../ui";
import { requireLogin } from "../../../security";
import { getProductType } from "../../../product";
import { userProductsQueryOptions, useRemoveProductMutation } from "../../../data/queries/products";
import { useDeleteProductMediaMutation } from "../../../data/queries/media";
import { useUser } from "../../../data/context/auth-context";
import type { Product } from "../../../data/api/products";

function Account() {
  const { name } = useParams();
  const user = useUser();
  const { data: products = [] } = useQuery(
    userProductsQueryOptions({
      userId: user?.uid || "",
      status: "draft",
      name,
    }),
  );
  const removeProductMutation = useRemoveProductMutation();
  const deleteMediaMutation = useDeleteProductMediaMutation();
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isDeleting = deleteMediaMutation.isPending || removeProductMutation.isPending;

  const handleDeleteClick = (product: Product) => () => {
    setDeleteError(null);
    setProductToDelete(product);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) {
      return;
    }

    setDeleteError(null);

    try {
      // Step 1: Delete all media files from storage
      const { errors } = await deleteMediaMutation.mutateAsync({
        productId: productToDelete.uid,
        userId: user?.uid,
      });

      if (errors.length > 0) {
        console.warn("Some media deletion errors:", errors);
      }

      // Step 2: Delete the product document
      await removeProductMutation.mutateAsync(productToDelete.uid);

      setProductToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Échec de la suppression");
    }
  };

  const handleDeleteCancel = () => {
    setProductToDelete(null);
  };

  const productType = getProductType(name || "");
  if (!productType) {
    return null;
  }

  return (
    <>
      <PageContent
        title={productType.labels.pendingTitle}
        description={productType.labels.pendingMessage}
        actions={
          <Link to={`/start/${name}?forceNew=true`}>
            <Button isPrimary>{productType.labels.newButton}</Button>
          </Link>
        }
      >
        {deleteError && (
          <Notice className={style.notice} status="error">
            {deleteError}
          </Notice>
        )}
        {products.length !== 0 && (
          <Notice className={style.notice} status="warning">
            {productType.labels.pendingMessage}
          </Notice>
        )}
        <div className={style.products}>
          {products.map((product) => (
            <div key={product.uid} className={style.product}>
              <Link to={`/settings/${product.uid}`} className={style.productPreview}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {productType.thumbnail(product.options as any)}
              </Link>
              <div className={style.productFooter}>
                <span className={style.productDate}>
                  {format(product.time, "MM/dd/yyyy HH:mm")}
                </span>

                <div className={style.buttons}>
                  <Button isBorderless onClick={handleDeleteClick(product)}>
                    <Trash />
                  </Button>
                  <Link to={`/settings/${product.uid}`}>
                    <Button isBorderless>
                      <Edit2 />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PageContent>

      <ConfirmDialog
        isOpen={!!productToDelete}
        title="Supprimer ce brouillon"
        message={
          <>
            <p>
              <strong>Attention:</strong> Cette action est irréversible.
            </p>
            <p>
              La suppression de ce brouillon effacera définitivement toutes les photos associées de
              notre serveur.
            </p>
            <p>Voulez-vous continuer?</p>
          </>
        }
        confirmLabel="Oui, supprimer"
        cancelLabel="Annuler"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}

export default requireLogin()(Account);
